<template>
  <div class="max-w-4xl bg-white rounded-lg shadow p-8">
    <h2 class="text-2xl font-bold mb-6">{{ isEdit ? 'Edit Operation' : 'Create New Operation' }}</h2>

    <form @submit.prevent="submitForm" class="space-y-6">
      <div v-if="error" class="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        {{ error }}
      </div>

      <!-- Service & Animal -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Service *</label>
          <select
            v-model="form.service_id"
            required
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Service</option>
            <option v-for="service in masterDataStore.services" :key="service.id" :value="service.id">
              {{ service.name }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Animal Type *</label>
          <select
            v-model="form.animal_id"
            required
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Animal</option>
            <option v-for="animal in masterDataStore.animals" :key="animal.id" :value="animal.id">
              {{ animal.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Colour & Sex -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Colour *</label>
          <select
            v-model="form.colour_id"
            required
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Colour</option>
            <option v-for="colour in masterDataStore.colours" :key="colour.id" :value="colour.id">
              {{ colour.name }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Sex *</label>
          <select
            v-model="form.sex"
            required
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Sex</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
      </div>

      <!-- Coordinates -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Latitude *</label>
          <input
            v-model.number="form.latitude"
            type="number"
            step="0.00001"
            required
            class="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Longitude *</label>
          <input
            v-model.number="form.longitude"
            type="number"
            step="0.00001"
            required
            class="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      <!-- Ward & Area -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Ward *</label>
          <input 
            v-model="form.ward" 
            class="w-full px-4 py-2 border border-gray-300 rounded-lg"
            required
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Area</label>
          <input 
            v-model="form.area"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      <!-- Reg ID (auto-generated) & Date -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Animal Reg ID</label>
          <input 
            :value="isEdit ? form.animal_reg_id : 'Auto-generated on save'"
            disabled
            class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Catching Date *</label>
          <input 
            v-model="form.catching_date"
            type="date"
            required
            class="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      <!-- Status & Remarks -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
        <select
          v-model="form.status_id"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option v-if="activeCatchingStatuses.length === 0" value="" disabled>No statuses configured</option>
          <option v-for="status in activeCatchingStatuses" :key="status.id" :value="status.id">
            {{ status.name }}
          </option>
        </select>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
        <textarea
          v-model="form.remarks"
          rows="4"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg"
          placeholder="Additional notes about the operation..."
        ></textarea>
      </div>

      <!-- Location Button -->
      <div class="bg-blue-50 p-4 rounded-lg">
        <button
          type="button"
          @click="getCurrentLocation"
          :disabled="locationLoading"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          📍 {{ locationLoading ? 'Getting location...' : 'Use Current Location' }}
        </button>
      </div>

      <!-- Submit -->
      <div class="flex justify-between">
        <router-link to="/operations/catchings" class="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50">
          Cancel
        </router-link>
        <button
          type="submit"
          :disabled="catchingStore.loading"
          class="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          {{ catchingStore.loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Operation' : 'Create Operation') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMasterDataStore } from '@/stores/masterDataStore'
import { useCatchingStore } from '@/stores/catchingStore'

const route = useRoute()
const router = useRouter()
const masterDataStore = useMasterDataStore()
const catchingStore = useCatchingStore()

const isEdit = computed(() => route.name === 'EditCatching')

const form = ref({
  service_id: '',
  animal_id: '',
  colour_id: '',
  catching_date: new Date().toISOString().split('T')[0],
  latitude: 19.0760,
  longitude: 72.8777,
  ward: '',
  area: '',
  animal_reg_id: '',
  sex: '',
  remarks: '',
  status_id: '',
  status: ''
})

const error = ref(null)
const locationLoading = ref(false)

const activeCatchingStatuses = computed(() => {
  return masterDataStore.statuses
    .filter(status => String(status.is_active) !== '0')
    .map(status => ({
      ...status,
      id: String(status.id || '').trim(),
      code: String(status.code || '').trim(),
      name: status.name || status.code,
      category: String(status.category || '').trim().toLowerCase(),
    }))
    .filter(status => status.id && status.code)
})

const statusValue = (catching) => {
  const rawStatus = String(catching?.status || '').trim()
  if (rawStatus) {
    const status = activeCatchingStatuses.value.find(item => item.id === rawStatus || item.code === rawStatus)
    return status?.id || rawStatus
  }
  const masterStatusId = String(catching?.status_master?.id || '').trim()
  if (masterStatusId) return masterStatusId
  return String(catching?.status_id || '').trim()
}

const defaultCaughtStatusId = () => {
  return activeCatchingStatuses.value.find(status => status.code === 'caught')?.id || ''
}

const getCurrentLocation = () => {
  if (!navigator.geolocation) {
    error.value = 'Geolocation not supported'
    return
  }
  
  locationLoading.value = true
  navigator.geolocation.getCurrentPosition(
    (position) => {
      form.value.latitude = position.coords.latitude
      form.value.longitude = position.coords.longitude
      locationLoading.value = false
    },
    (err) => {
      error.value = 'Failed to get location: ' + err.message
      locationLoading.value = false
    }
  )
}

const submitForm = async () => {
  error.value = null
  try {
    const payload = {
      ...form.value,
      status: form.value.status_id,
    }
    let response
    if (isEdit.value) {
      response = await catchingStore.updateCatching(route.params.id, payload)
    } else {
      response = await catchingStore.createCatching(payload)
    }
    router.push(`/operations/catchings/${response.id || route.params.id}`)
  } catch (err) {
    error.value = err.response?.data?.message || (isEdit.value ? 'Failed to update operation' : 'Failed to create operation')
  }
}

onMounted(async () => {
  await masterDataStore.loadAll()
  
  if (isEdit.value) {
    await catchingStore.getCatching(route.params.id)
    if (catchingStore.currentCatching) {
      form.value = { ...catchingStore.currentCatching }
      form.value.status_id = statusValue(catchingStore.currentCatching)
    }
  } else {
    form.value.status_id = defaultCaughtStatusId()
  }
})
</script>
